import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, bigserial, boolean, timestamp, jsonb, primaryKey, unique, index, type AnyPgColumn } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

import { tenants } from './common';
import { partnerAccessProfiles } from './partner';

export const companyTypeDefinitions = pgTable("company_type_definitions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  slug: text("slug").notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),
  capabilitySummary: jsonb("capability_summary").$type<string[]>().default([]),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  defaultAccessProfileId: text("default_access_profile_id").references((): AnyPgColumn => partnerAccessProfiles.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCompanyTypeDefinitionSchema = createInsertSchema(companyTypeDefinitions).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertCompanyTypeDefinition = z.infer<typeof insertCompanyTypeDefinitionSchema>;
export type CompanyTypeDefinition = typeof companyTypeDefinitions.$inferSelect;
// Pack 9.H — Task #1019, draft #733 closure.
// Rename-safe FK to company_type_definitions: the FK on type_slug is updated
// via ON UPDATE CASCADE so renaming a slug propagates to every tenant link
// instead of breaking referential integrity. The stable surrogate identifier
// (`definitionId`) is also captured to support a future rename-by-id surface
// (slug becomes a display key only). Backfill performed by migration 0091.
export const tenantCompanyTypes = pgTable("tenant_company_types", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  // Stable-id reference to the canonical row. Populated by migration 0091.
  definitionId: varchar("definition_id").references((): AnyPgColumn => companyTypeDefinitions.id, { onDelete: "cascade" }),
  // Slug retained as the human-friendly identifier; rename-safe via ON UPDATE CASCADE.
  typeSlug: text("type_slug").notNull().references(() => companyTypeDefinitions.slug, { onDelete: "cascade", onUpdate: "cascade" }),
  customLabel: text("custom_label"),
  customDescription: text("custom_description"),
  isEnabled: boolean("is_enabled").notNull().default(true),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  uniq: unique().on(table.tenantId, table.typeSlug),
}));
export const insertTenantCompanyTypeSchema = createInsertSchema(tenantCompanyTypes).omit({ id: true, updatedAt: true });
export type InsertTenantCompanyType = z.infer<typeof insertTenantCompanyTypeSchema>;
export type TenantCompanyType = typeof tenantCompanyTypes.$inferSelect;

// ─── End Company Type Registry ──────────────────────────────────────────────
export const INDUSTRY_TYPES = ["oil_gas", "construction", "mining", "manufacturing", "maritime", "general"] as const;
export type IndustryType = typeof INDUSTRY_TYPES[number];
export const CONFIG_PACK_MODULES = ["ptw", "safety", "credentials", "equipment", "access", "notifications", "approval_matrix"] as const;
export type ConfigPackModule = typeof CONFIG_PACK_MODULES[number];
export const configurationPacks = pgTable("configuration_packs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  industryType: text("industry_type").notNull().default("general"),
  version: integer("version").notNull().default(1),
  status: text("status").notNull().default("draft"),
  description: text("description"),
  isSystem: boolean("is_system").notNull().default(false),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  // Soft-delete metadata (deleted_at IS NOT NULL ⇒ hidden from normal read paths,
  // recoverable via /restore until the retention job purges).
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
  deletedBy: text("deleted_by"),
});
export const insertConfigurationPackSchema = createInsertSchema(configurationPacks).omit({ id: true, createdAt: true, updatedAt: true, publishedAt: true, deletedAt: true, deletedBy: true });
export type InsertConfigurationPack = z.infer<typeof insertConfigurationPackSchema>;
export type ConfigurationPack = typeof configurationPacks.$inferSelect;

export const configPackVersions = pgTable("config_pack_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packId: text("pack_id").notNull().references(() => configurationPacks.id, { onDelete: "cascade" }),
  version: integer("version").notNull(),
  changelog: text("changelog"),
  snapshot: jsonb("snapshot").$type<Record<string, any>>().notNull().default({}),
  publishedAt: timestamp("published_at", { withTimezone: true }).defaultNow(),
  publishedBy: text("published_by"),
  publishedByName: text("published_by_name"),
}, (t) => ({
  uniqPackVersion: unique("uq_config_pack_versions_pack_version").on(t.packId, t.version),
}));
export type ConfigPackVersion = typeof configPackVersions.$inferSelect;

export const configurationPackItems = pgTable("configuration_pack_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packId: text("pack_id").notNull().references(() => configurationPacks.id, { onDelete: "cascade" }),
  module: text("module").notNull(),
  itemType: text("item_type").notNull(),
  itemData: jsonb("item_data").$type<Record<string, any>>().notNull().default({}),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  requiredFeatureKey: text("required_feature_key"),
});
export const insertConfigurationPackItemSchema = createInsertSchema(configurationPackItems).omit({ id: true, createdAt: true });
export type InsertConfigurationPackItem = z.infer<typeof insertConfigurationPackItemSchema>;
export type ConfigurationPackItem = typeof configurationPackItems.$inferSelect;
export const tenantAppliedPacks = pgTable("tenant_applied_packs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  packId: text("pack_id").notNull().references(() => configurationPacks.id),
  packVersion: integer("pack_version").notNull().default(1),
  appliedAt: timestamp("applied_at", { withTimezone: true }).defaultNow(),
  appliedBy: text("applied_by"),
  configSnapshot: jsonb("config_snapshot").$type<{ featureStates: Array<{ featureKey: string; enabled: boolean; config: Record<string, any> | null; previewExpiresAt: string | null }> } | null>(),
});
export const insertTenantAppliedPackSchema = createInsertSchema(tenantAppliedPacks).omit({ id: true, appliedAt: true });
export type InsertTenantAppliedPack = z.infer<typeof insertTenantAppliedPackSchema>;
export type TenantAppliedPack = typeof tenantAppliedPacks.$inferSelect;
export const tenantSetupSkips = pgTable("tenant_setup_skips", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  itemKey: text("item_key").notNull(),
  skippedAt: timestamp("skipped_at", { withTimezone: true }).defaultNow(),
  skippedBy: text("skipped_by"),
});
export const insertTenantSetupSkipSchema = createInsertSchema(tenantSetupSkips).omit({ id: true, skippedAt: true });
export type InsertTenantSetupSkip = z.infer<typeof insertTenantSetupSkipSchema>;
export type TenantSetupSkip = typeof tenantSetupSkips.$inferSelect;

// ─── Platform Subscription Tiers ─────────────────────────────────────────────
export const subscriptionTiers = pgTable("subscription_tiers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),
  tagline: text("tagline"),
  rank: integer("rank").notNull(),
  isBuiltIn: boolean("is_built_in").notNull().default(false),
  isDefault: boolean("is_default").notNull().default(false),
  isRetired: boolean("is_retired").notNull().default(false),
  highlightColor: text("highlight_color"),
  featureKeys: text("feature_keys").array().default([]),
  entitlementLimits: jsonb("entitlement_limits").$type<{
    maxActiveWorkers?: number | null;
    maxPortalUsers?: number | null;
    maxSites?: number | null;
    maxApiCallsPerMonth?: number | null;
    maxBulkJobSize?: number | null;
  }>().default({}),
  pricingDisplay: jsonb("pricing_display").$type<{
    monthlyLabel?: string;
    annualLabel?: string;
    note?: string;
  }>().default({}),
  comparisonHighlights: text("comparison_highlights").array().default([]),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertSubscriptionTierSchema = createInsertSchema(subscriptionTiers).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertSubscriptionTier = z.infer<typeof insertSubscriptionTierSchema>;
export type SubscriptionTier = typeof subscriptionTiers.$inferSelect;

// ─── Tenant Usage Counters ────────────────────────────────────────────────────
export const tenantUsageCounters = pgTable("tenant_usage_counters", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().unique().references(() => tenants.id, { onDelete: "cascade" }),
  activeWorkers: integer("active_workers").notNull().default(0),
  portalUsers: integer("portal_users").notNull().default(0),
  activeSites: integer("active_sites").notNull().default(0),
  monthlyApiCalls: integer("monthly_api_calls").notNull().default(0),
  bulkJobCount: integer("bulk_job_count").notNull().default(0),
  storageUsedMb: integer("storage_used_mb").notNull().default(0),
  apiCallsResetAt: timestamp("api_calls_reset_at", { withTimezone: true }).defaultNow(),
  lastReconciled: timestamp("last_reconciled", { withTimezone: true }),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertTenantUsageCounterSchema = createInsertSchema(tenantUsageCounters).omit({ id: true, updatedAt: true });
export type InsertTenantUsageCounter = z.infer<typeof insertTenantUsageCounterSchema>;
export type TenantUsageCounter = typeof tenantUsageCounters.$inferSelect;

// ─── Tenant Usage Snapshots (daily) ─────────────────────────────────────────
export const tenantUsageSnapshots = pgTable("tenant_usage_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  snapshotDate: text("snapshot_date").notNull(),
  activeWorkers: integer("active_workers").notNull().default(0),
  portalUsers: integer("portal_users").notNull().default(0),
  activeSites: integer("active_sites").notNull().default(0),
  monthlyApiCalls: integer("monthly_api_calls").notNull().default(0),
  bulkJobCount: integer("bulk_job_count").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqTenantDate: unique("tenant_usage_snapshots_tenant_date").on(t.tenantId, t.snapshotDate),
}));
export const insertTenantUsageSnapshotSchema = createInsertSchema(tenantUsageSnapshots).omit({ id: true, createdAt: true });
export type InsertTenantUsageSnapshot = z.infer<typeof insertTenantUsageSnapshotSchema>;
export type TenantUsageSnapshot = typeof tenantUsageSnapshots.$inferSelect;

// ─── Add-On Modules ─────────────────────────────────────────────────────────
export const addonModules = pgTable("addon_modules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),
  featureKeys: text("feature_keys").array().default([]),
  entitlementGrants: jsonb("entitlement_grants").$type<{
    additionalWorkers?: number;
    additionalUsers?: number;
    additionalSites?: number;
    additionalApiCalls?: number;
  }>().default({}),
  compatibleTierKeys: text("compatible_tier_keys").array().default([]),
  displayPricing: jsonb("display_pricing").$type<{ label?: string; note?: string }>(),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertAddonModuleSchema = createInsertSchema(addonModules).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertAddonModule = z.infer<typeof insertAddonModuleSchema>;
export type AddonModule = typeof addonModules.$inferSelect;

// ─── Tenant Add-On Activations ─────────────────────────────────────────────
export const tenantAddonActivations = pgTable("tenant_addon_activations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  addonKey: text("addon_key").notNull().references(() => addonModules.key, { onDelete: "cascade" }),
  activatedAt: timestamp("activated_at", { withTimezone: true }).defaultNow(),
  activatedBy: text("activated_by"),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  isActive: boolean("is_active").notNull().default(true),
  notes: text("notes"),
}, (t) => ({
  uniqTenantAddon: unique("tenant_addon_activations_tenant_addon").on(t.tenantId, t.addonKey),
}));
export const insertTenantAddonActivationSchema = createInsertSchema(tenantAddonActivations).omit({ id: true, activatedAt: true });
export type InsertTenantAddonActivation = z.infer<typeof insertTenantAddonActivationSchema>;
export type TenantAddonActivation = typeof tenantAddonActivations.$inferSelect;

// ─── Tenant Entitlement Overrides ─────────────────────────────────────────
export const tenantEntitlementOverrides = pgTable("tenant_entitlement_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  entitlementKey: text("entitlement_key").notNull(),
  overrideValue: integer("override_value"),
  reason: text("reason"),
  setBy: text("set_by"),
  setAt: timestamp("set_at", { withTimezone: true }).defaultNow(),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
}, (t) => ({
  uniqTenantEntitlement: unique("tenant_entitlement_overrides_tenant_key").on(t.tenantId, t.entitlementKey),
}));
export const insertTenantEntitlementOverrideSchema = createInsertSchema(tenantEntitlementOverrides).omit({ id: true, setAt: true });
export type InsertTenantEntitlementOverride = z.infer<typeof insertTenantEntitlementOverrideSchema>;
export type TenantEntitlementOverride = typeof tenantEntitlementOverrides.$inferSelect;

// ─── Commercial Audit Log ─────────────────────────────────────────────────
export const commercialAuditLog = pgTable("commercial_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id"),
  eventType: text("event_type").notNull(),
  actorId: text("actor_id"),
  actorName: text("actor_name"),
  previousState: jsonb("previous_state").$type<Record<string, any>>(),
  newState: jsonb("new_state").$type<Record<string, any>>(),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertCommercialAuditLogSchema = createInsertSchema(commercialAuditLog).omit({ id: true, createdAt: true });
export type InsertCommercialAuditLog = z.infer<typeof insertCommercialAuditLogSchema>;
export type CommercialAuditLogEntry = typeof commercialAuditLog.$inferSelect;

// ─── Platform Feature Config (per-feature overrides) ─────────────────────────
export const platformFeatureConfig = pgTable("platform_feature_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  featureKey: text("feature_key").notNull().unique(),
  minTierOverride: text("min_tier_override"),
  globalEnabled: boolean("global_enabled"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  updatedBy: text("updated_by"),
});
export type PlatformFeatureConfig = typeof platformFeatureConfig.$inferSelect;

// ─── Platform Config Audit Log ────────────────────────────────────────────────
export const platformConfigAuditLog = pgTable("platform_config_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  entityType: text("entity_type").notNull(),
  entityKey: text("entity_key").notNull(),
  field: text("field").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  actorId: text("actor_id"),
  actorName: text("actor_name"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertPlatformConfigAuditLogSchema = createInsertSchema(platformConfigAuditLog).omit({ id: true, createdAt: true });
export type InsertPlatformConfigAuditLog = z.infer<typeof insertPlatformConfigAuditLogSchema>;
export type PlatformConfigAuditLogEntry = typeof platformConfigAuditLog.$inferSelect;

// ─── Password Reset Tokens ────────────────────────────────────────────────────
export const tenantOnboardingWarnings = pgTable("tenant_onboarding_warnings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  failedModules: text("failed_modules").array().notNull().default([]),
  details: jsonb("details").$type<Record<string, string>>().notNull().default({}),
  dismissed: boolean("dismissed").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  dismissedAt: timestamp("dismissed_at", { withTimezone: true }),
});
export const insertTenantOnboardingWarningSchema = createInsertSchema(tenantOnboardingWarnings).omit({ id: true, createdAt: true, dismissedAt: true });
export type InsertTenantOnboardingWarning = z.infer<typeof insertTenantOnboardingWarningSchema>;
export type TenantOnboardingWarning = typeof tenantOnboardingWarnings.$inferSelect;
export const tenantBusinessConfigs = pgTable("tenant_business_configs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id).unique(),

  // Compliance Rules: credential expiry warnings, document retention, verification strictness
  // Extended to include blueprint-propagated enforcement fields:
  //   autoSuspendOnExpiry, requireDualApproval, autoEscalateAfterHours, maxPermitDurationHours
  complianceRules: jsonb("compliance_rules").$type<{
    credentialExpiryWarningTiers: number[];
    documentRetentionDays: number;
    verificationStrictnessLevel: "low" | "medium" | "high";
    autoSuspendOnExpiry?: boolean;
    requireDualApproval?: boolean;
    autoEscalateAfterHours?: number;
    maxPermitDurationHours?: number;
  }>().default({ credentialExpiryWarningTiers: [24, 48, 72], documentRetentionDays: 365, verificationStrictnessLevel: "medium" }),

  // Access Rules: rapid re-entry detection, anomaly sensitivity, non-overridable dimensions
  accessRules: jsonb("access_rules").$type<{
    rapidReentryWindowSeconds: number;
    anomalySensitivityThreshold: "low" | "medium" | "high";
    nonOverridableDimensions: string[];
  }>().default({ rapidReentryWindowSeconds: 120, anomalySensitivityThreshold: "medium", nonOverridableDimensions: ["identity_mismatch", "credential_revoked", "company_blacklisted", "emergency_lockdown"] }),

  // Onboarding Rules: required steps per worker type, completeness thresholds, valid transitions
  onboardingRules: jsonb("onboarding_rules").$type<{
    requiredStepsForSubmission: string[];
    requiredStepsForApproval: string[];
    requiredStepsForActivation: string[];
    validTransitions: Record<string, string[]>;
  }>().default({
    requiredStepsForSubmission: ["identityDataComplete", "personalDetailsComplete", "emergencyContactComplete", "employmentDataComplete"],
    requiredStepsForApproval: ["identityDataComplete", "personalDetailsComplete", "emergencyContactComplete", "employmentDataComplete", "documentComplete", "credentialEvidenceComplete"],
    requiredStepsForActivation: ["identityDataComplete", "personalDetailsComplete", "emergencyContactComplete", "employmentDataComplete", "documentComplete", "credentialEvidenceComplete", "readinessRequirementsAssigned"],
    validTransitions: { draft: ["submitted"], submitted: ["in_review", "draft"], in_review: ["approved", "rejected", "submitted"], approved: ["activated", "in_review"], rejected: ["draft"], activated: [] },
  }),

  // Lifecycle Rules: deployment transition effects, demobilization checklist, suspension behaviors
  lifecycleRules: jsonb("lifecycle_rules").$type<{
    deploymentTransitionEffects: Record<string, { access: string; ptw: string; wallet: string }>;
    mandatoryDemobilizationChecklistItems: string[];
    suspensionLevelBehaviors: Record<string, { revokeAccess: boolean; suspendPtw: boolean; freezeWallet: boolean }>;
  }>().default({
    deploymentTransitionEffects: {
      "mobilized→deployed": { access: "Site access granted", ptw: "Eligible for permit requests", wallet: "Wallet activated" },
      "deployed→suspended": { access: "All site access revoked immediately", ptw: "Active permits suspended", wallet: "Wallet frozen - no new credentials" },
      "deployed→demobilized": { access: "Access deactivated", ptw: "All permits closed", wallet: "Wallet archived" },
    },
    mandatoryDemobilizationChecklistItems: ["returnEquipmentCheck", "supervisorAcknowledgement", "companyAcknowledgement"],
    suspensionLevelBehaviors: {
      site: { revokeAccess: true, suspendPtw: true, freezeWallet: false },
      global: { revokeAccess: true, suspendPtw: true, freezeWallet: true },
    },
  }),

  // Readiness Rules: base requirements, role-based additions, evaluation model
  readinessRules: jsonb("readiness_rules").$type<{
    baseRequirements: string[];
    evaluationModel: "all-of" | "any-of" | "threshold";
    expiringSoonDays: number;
    overrideAllowedWhenMissingCount: number;
  }>().default({
    baseRequirements: ["Identity Verification", "General Safety Induction", "Medical Fitness Certificate"],
    evaluationModel: "all-of",
    expiringSoonDays: 30,
    overrideAllowedWhenMissingCount: 2,
  }),

  // PTW Phase 3.B (Task #1261): Segregation-of-Duties policy list. Each
  // entry is a `PtwSodPolicy` (`shared/ptw-sod.ts`). When the array is
  // empty (or the column is left at its default) the engine falls back to
  // `DEFAULT_PTW_SOD_POLICIES`. Tenants opt out by storing an empty array
  // with origin = "custom".
  ptwSodPolicies: jsonb("ptw_sod_policies").$type<Array<{
    id: string;
    name: string;
    description?: string;
    enabled: boolean;
    ruleType:
      | "different_actor_per_role"
      | "different_actor_per_action"
      | "exclusive_actor_for_actions"
      | "different_company_from_requestor";
    scope: "same_permit";
    roleA?: string;
    roleB?: string;
    actionA?: string;
    actionB?: string;
    actionsApplied?: string[];
    overrideMode: "deny" | "warn_only" | "allow_with_justification";
    justificationMinLength?: number;
    priority: number;
  }>>().notNull().default([]),

  // Origin tracking for each domain: "platform_default" | "custom"
  complianceRulesOrigin: text("compliance_rules_origin").notNull().default("platform_default"),
  accessRulesOrigin: text("access_rules_origin").notNull().default("platform_default"),
  onboardingRulesOrigin: text("onboarding_rules_origin").notNull().default("platform_default"),
  lifecycleRulesOrigin: text("lifecycle_rules_origin").notNull().default("platform_default"),
  readinessRulesOrigin: text("readiness_rules_origin").notNull().default("platform_default"),
  ptwSodPoliciesOrigin: text("ptw_sod_policies_origin").notNull().default("platform_default"),

  // Pack 9.E (#737, #738): optimistic concurrency for safe-edit. PUT callers
  // must echo the current `version`; stale writes return 409 VERSION_MISMATCH.
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertTenantBusinessConfigSchema = createInsertSchema(tenantBusinessConfigs).omit({ id: true, createdAt: true, updatedAt: true, version: true });
export type InsertTenantBusinessConfig = z.infer<typeof insertTenantBusinessConfigSchema>;
export type TenantBusinessConfig = typeof tenantBusinessConfigs.$inferSelect;

// ─── Platform Business Rules Defaults ────────────────────────────────────────
// Stores platform-wide default values for business rules.
// Tenants without custom overrides inherit these values.
export const platformBusinessRulesDefaults = pgTable("platform_business_rules_defaults", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  domain: text("domain").notNull().unique(),
  rules: jsonb("rules").$type<Record<string, any>>().notNull(),
  updatedBy: text("updated_by"),
  // Pack 9.E (#737, #738): optimistic concurrency for safe-edit.
  version: integer("version").notNull().default(1),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertPlatformBusinessRulesDefaultsSchema = createInsertSchema(platformBusinessRulesDefaults).omit({ id: true, createdAt: true, updatedAt: true, version: true });
export type InsertPlatformBusinessRulesDefaults = z.infer<typeof insertPlatformBusinessRulesDefaultsSchema>;
export type PlatformBusinessRulesDefaults = typeof platformBusinessRulesDefaults.$inferSelect;

// ─── Role Permission Overrides (Pack 2.X.1 — durable role/permission edits) ─
// Stores per-tenant role × permission overrides. Tenant id "platform" is the
// sentinel for super-admin platform-wide overrides (no specific active tenant).
// Backs the admin role catalog UI; replaces the in-process Map used in Pack 1.
export const rolePermissionOverrides = pgTable("role_permission_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().default("platform"),
  roleName: text("role_name").notNull(),
  permissions: text("permissions").array().notNull().default(sql`ARRAY[]::text[]`),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  updatedBy: text("updated_by"),
}, (t) => ({
  uniqTenantRole: unique("uq_role_permission_overrides_tenant_role").on(t.tenantId, t.roleName),
}));
export const insertRolePermissionOverrideSchema = createInsertSchema(rolePermissionOverrides).omit({ id: true, updatedAt: true });
export type InsertRolePermissionOverride = z.infer<typeof insertRolePermissionOverrideSchema>;
export type RolePermissionOverride = typeof rolePermissionOverrides.$inferSelect;

// ─── Business Rules Audit Log ─────────────────────────────────────────────────
// Records every change to business rules with actor and previous value.
export const businessRulesAuditLog = pgTable("business_rules_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  entityType: text("entity_type").notNull(),
  entityId: text("entity_id").notNull(),
  domain: text("domain").notNull(),
  actorId: text("actor_id"),
  actorName: text("actor_name"),
  previousValue: jsonb("previous_value").$type<Record<string, any>>(),
  newValue: jsonb("new_value").$type<Record<string, any>>(),
  action: text("action").notNull().default("update"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertBusinessRulesAuditLogSchema = createInsertSchema(businessRulesAuditLog).omit({ id: true, createdAt: true });
export type InsertBusinessRulesAuditLog = z.infer<typeof insertBusinessRulesAuditLogSchema>;
export type BusinessRulesAuditLogEntry = typeof businessRulesAuditLog.$inferSelect;

// ─── Industry Blueprints ──────────────────────────────────────────────────────
// Versioned configuration blueprints for different industries. Each blueprint
// encodes best-practice defaults for features, business rules, credentials,
// workflows, safety presets, and more.
export const industryBlueprints = pgTable("industry_blueprints", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  description: text("description"),
  industryTags: text("industry_tags").array().default([]),
  isPlatformBlueprint: boolean("is_platform_blueprint").notNull().default(true),
  currentVersion: integer("current_version").notNull().default(1),
  rowVersion: integer("row_version").notNull().default(1),
  status: text("status").notNull().default("draft"),
  authorId: text("author_id"),
  authorName: text("author_name"),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  featureSet: jsonb("feature_set").$type<{
    enabled: string[];
    disabled: string[];
    config: Record<string, any>;
  }>().default({ enabled: [], disabled: [], config: {} }),
  businessRules: jsonb("business_rules").$type<Record<string, any>>().default({}),
  credentialBundle: jsonb("credential_bundle").$type<Array<{
    templateId?: string;
    templateName: string;
    category: string;
    required: boolean;
  }>>().default([]),
  workflowTemplates: jsonb("workflow_templates").$type<Array<{
    name: string;
    workType: string;
    stages: string[];
    description?: string;
  }>>().default([]),
  orchestrationStarters: jsonb("orchestration_starters").$type<Array<{
    name: string;
    trigger: string;
    action: string;
    description?: string;
  }>>().default([]),
  safetyPresets: jsonb("safety_presets").$type<Array<{
    name: string;
    color: string;
    level: number;
    escalationThresholdHours?: number;
  }>>().default([]),
  ptwRoleDefaults: jsonb("ptw_role_defaults").$type<Array<{
    jobTitle: string;
    ptwRole: string;
  }>>().default([]),
  companyTypeDefaults: jsonb("company_type_defaults").$type<Array<{
    slug: string;
    label: string;
    enabled: boolean;
  }>>().default([]),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertIndustryBlueprintSchema = createInsertSchema(industryBlueprints).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertIndustryBlueprint = z.infer<typeof insertIndustryBlueprintSchema>;
export type IndustryBlueprint = typeof industryBlueprints.$inferSelect;

// Blueprint version history — each published version is stored immutably
export const blueprintVersions = pgTable("blueprint_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  blueprintId: text("blueprint_id").notNull().references(() => industryBlueprints.id, { onDelete: "cascade" }),
  version: integer("version").notNull(),
  changelog: text("changelog"),
  snapshot: jsonb("snapshot").$type<Record<string, any>>().notNull().default({}),
  publishedAt: timestamp("published_at", { withTimezone: true }).defaultNow(),
  publishedBy: text("published_by"),
  publishedByName: text("published_by_name"),
});
export const insertBlueprintVersionSchema = createInsertSchema(blueprintVersions).omit({
  id: true,
  publishedAt: true,
});
export type InsertBlueprintVersion = z.infer<typeof insertBlueprintVersionSchema>;
export type BlueprintVersion = typeof blueprintVersions.$inferSelect;

// Tracks which tenants are using which blueprint version
export const tenantBlueprintAssignments = pgTable("tenant_blueprint_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  blueprintId: text("blueprint_id").notNull().references(() => industryBlueprints.id),
  appliedVersion: integer("applied_version").notNull(),
  appliedAt: timestamp("applied_at", { withTimezone: true }).defaultNow(),
  appliedBy: text("applied_by"),
  appliedByName: text("applied_by_name"),
  overrides: jsonb("overrides").$type<Record<string, any>>().default({}),
  status: text("status").notNull().default("active"),
  dismissedVersion: integer("dismissed_version"),
});
export const insertTenantBlueprintAssignmentSchema = createInsertSchema(tenantBlueprintAssignments).omit({
  id: true,
  appliedAt: true,
});
export type InsertTenantBlueprintAssignment = z.infer<typeof insertTenantBlueprintAssignmentSchema>;
export type TenantBlueprintAssignment = typeof tenantBlueprintAssignments.$inferSelect;

// Permission catalog overrides — super-admin annotations and metadata overrides per permission key
export const permissionCatalogOverrides = pgTable("permission_catalog_overrides", {
  key: text("key").primaryKey(),
  note: text("note"),
  descriptionOverride: text("description_override"),
  riskLevelOverride: text("risk_level_override"),
  delegatableOverride: boolean("delegatable_override"),
  hidden: boolean("hidden").notNull().default(false),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  updatedBy: text("updated_by"),
});
export type PermissionCatalogOverride = typeof permissionCatalogOverrides.$inferSelect;

// Partner Agreements — super-admin managed agreements with partner companies
export const globalRoles = pgTable("global_roles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  description: text("description"),
  permissions: jsonb("permissions").$type<string[]>().notNull().default([]),
  isBuiltIn: boolean("is_built_in").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertGlobalRoleSchema = createInsertSchema(globalRoles).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertGlobalRole = z.infer<typeof insertGlobalRoleSchema>;
export type GlobalRole = typeof globalRoles.$inferSelect;

// ─── Background Jobs ──────────────────────────────────────────────────────────
// Used by blueprint push-to-all async processing (server/routes.ts, server/index.ts).
// Created by migration 0036_scalability_indexes.sql.
// Enhanced for SA-24: Bulk Operations & Background Job Framework.
export const BULK_JOB_TYPES = [
  "blueprint_push_all", "tenant_feature_rollout", "tier_migration",
  "compliance_reset", "session_purge", "bulk_partner_onboard",
  "partner_bulk_worker_onboard",
] as const;
export type BulkJobType = typeof BULK_JOB_TYPES[number];
// "running" is the legacy status used by blueprint_push_all jobs created directly via raw SQL.
// All new bulk-operation-service jobs use "processing" instead.
export const BULK_JOB_STATUSES = ["queued", "processing", "running", "completed", "failed", "cancelled", "retrying"] as const;
export type BulkJobStatus = typeof BULK_JOB_STATUSES[number];

export interface BulkJobErrorDetail {
  itemId: string;
  itemLabel?: string;
  error: string;
  timestamp: string;
  retryable: boolean;
}

export const backgroundJobs = pgTable("background_jobs", {
  id: varchar("id").primaryKey(),
  jobType: text("job_type").notNull(),
  status: text("status").notNull().default("queued"),
  payload: jsonb("payload").$type<Record<string, any>>().default({}),
  result: jsonb("result").$type<Record<string, any>>(),
  processedCount: integer("processed_count").notNull().default(0),
  totalCount: integer("total_count").notNull().default(0),
  errorMessage: text("error_message"),
  errorDetails: jsonb("error_details").$type<BulkJobErrorDetail[]>().default([]),
  retryCount: integer("retry_count").notNull().default(0),
  maxRetries: integer("max_retries").notNull().default(3),
  lastRetryAt: timestamp("last_retry_at", { withTimezone: true }),
  cancelledAt: timestamp("cancelled_at", { withTimezone: true }),
  cancelledBy: text("cancelled_by"),
  parentJobId: text("parent_job_id"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  startedAt: timestamp("started_at", { withTimezone: true }),
  completedAt: timestamp("completed_at", { withTimezone: true }),
  estimatedCompletionAt: timestamp("estimated_completion_at", { withTimezone: true }),
  heartbeatAt: timestamp("heartbeat_at", { withTimezone: true }),
  reapedAt: timestamp("reaped_at", { withTimezone: true }),
  reapedReason: text("reaped_reason"),
}, (t) => ({
  idxBackgroundJobsStatus: index("idx_background_jobs_status").on(t.status),
  idxBackgroundJobsJobType: index("idx_background_jobs_job_type").on(t.jobType),
  idxBackgroundJobsCreatedAt: index("idx_background_jobs_created_at").on(t.createdAt),
  idxBackgroundJobsParent: index("idx_background_jobs_parent").on(t.parentJobId),
  idxBackgroundJobsHeartbeat: index("idx_background_jobs_heartbeat").on(t.heartbeatAt),
  // Pack 5 (Task #924): backport of legacy migration 0075_sa_perf_indexes idx_background_jobs_status_created
  idxBackgroundJobsStatusCreated: index("idx_background_jobs_status_created").on(t.status, t.createdAt),
}));
export const insertBackgroundJobSchema = createInsertSchema(backgroundJobs).omit({ createdAt: true, updatedAt: true });
export type InsertBackgroundJob = z.infer<typeof insertBackgroundJobSchema>;
export type BackgroundJob = typeof backgroundJobs.$inferSelect;
// ─── Workflow Templates (canonical model) ────────────────────────────────────
export const WORKFLOW_CATEGORY_KEYS = ["ptw", "onboarding", "timesheet", "inspection", "orchestration"] as const;
export type WorkflowCategoryKey = typeof WORKFLOW_CATEGORY_KEYS[number];
export const WORKFLOW_TEMPLATE_STATUSES = ["draft", "published", "retired"] as const;
export type WorkflowTemplateStatus = typeof WORKFLOW_TEMPLATE_STATUSES[number];
export const ADOPTION_MODELS = ["copy", "inherit", "linked"] as const;
export type AdoptionModel = typeof ADOPTION_MODELS[number];

export const workflowTemplates = pgTable("workflow_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  categoryKey: text("category_key").notNull(),
  name: text("name").notNull(),
  version: integer("version").notNull().default(1),
  status: text("status").notNull().default("draft"),
  ownerScope: text("owner_scope").notNull().default("platform"),
  adoptionModel: text("adoption_model").notNull().default("copy"),
  configuration: jsonb("configuration").$type<Record<string, any>>().notNull().default({}),
  compatibilityMetadata: jsonb("compatibility_metadata").$type<Record<string, any>>().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  publishedAt: timestamp("published_at", { withTimezone: true }),
  publishedBy: text("published_by"),
}, (t) => ({
  idxWorkflowTemplatesCategory: index("idx_workflow_templates_category").on(t.categoryKey),
  idxWorkflowTemplatesStatus: index("idx_workflow_templates_status").on(t.status),
}));
export const insertWorkflowTemplateSchema = createInsertSchema(workflowTemplates).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertWorkflowTemplate = z.infer<typeof insertWorkflowTemplateSchema>;
export type WorkflowTemplate = typeof workflowTemplates.$inferSelect;

export const tenantWorkflowAssignments = pgTable("tenant_workflow_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  templateId: text("template_id").notNull().references(() => workflowTemplates.id, { onDelete: "cascade" }),
  appliedVersion: integer("applied_version").notNull().default(1),
  overrides: jsonb("overrides").$type<Record<string, any>>().default({}),
  assignedAt: timestamp("assigned_at", { withTimezone: true }).defaultNow(),
  assignedBy: text("assigned_by"),
}, (t) => ({
  uniqTenantTemplate: unique("tenant_workflow_assignments_tenant_template").on(t.tenantId, t.templateId),
  idxTenantWorkflowAssignmentsTenant: index("idx_tenant_workflow_assignments_tenant").on(t.tenantId),
}));
export const insertTenantWorkflowAssignmentSchema = createInsertSchema(tenantWorkflowAssignments).omit({ id: true, assignedAt: true });
export type InsertTenantWorkflowAssignment = z.infer<typeof insertTenantWorkflowAssignmentSchema>;
export type TenantWorkflowAssignment = typeof tenantWorkflowAssignments.$inferSelect;

// ─── Workflow Template Versions (immutable snapshots on publish) ─────────────
export const workflowTemplateVersions = pgTable("workflow_template_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: text("template_id").notNull().references(() => workflowTemplates.id, { onDelete: "cascade" }),
  version: integer("version").notNull(),
  changelog: text("changelog"),
  snapshot: jsonb("snapshot").$type<Record<string, any>>().notNull().default({}),
  publishedAt: timestamp("published_at", { withTimezone: true }).defaultNow(),
  publishedBy: text("published_by"),
  publishedByName: text("published_by_name"),
}, (t) => ({
  uniqTemplateVersion: unique("uq_workflow_template_versions_template_version").on(t.templateId, t.version),
  idxWorkflowTemplateVersionsTemplate: index("idx_workflow_template_versions_template").on(t.templateId),
}));
export type WorkflowTemplateVersion = typeof workflowTemplateVersions.$inferSelect;

// ─── Workflow Template Audit Log ────────────────────────────────────────────
export const workflowTemplateAuditLog = pgTable("workflow_template_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: text("template_id").notNull(),
  categoryKey: text("category_key").notNull(),
  action: text("action").notNull(), // created | updated | published | retired | duplicated | deleted
  actorId: text("actor_id"),
  actorName: text("actor_name"),
  previousValue: jsonb("previous_value").$type<Record<string, any>>(),
  newValue: jsonb("new_value").$type<Record<string, any>>(),
  changelog: text("changelog"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxWorkflowTemplateAuditTemplate: index("idx_workflow_template_audit_template").on(t.templateId),
  idxWorkflowTemplateAuditCreated: index("idx_workflow_template_audit_created").on(t.createdAt),
}));
export const insertWorkflowTemplateAuditLogSchema = createInsertSchema(workflowTemplateAuditLog).omit({ id: true, createdAt: true });
export type InsertWorkflowTemplateAuditLog = z.infer<typeof insertWorkflowTemplateAuditLogSchema>;
export type WorkflowTemplateAuditLogEntry = typeof workflowTemplateAuditLog.$inferSelect;

export const schedulerLocks = pgTable("scheduler_locks", {
  key: text("key").primaryKey(),
  lockToken: text("lock_token"),
  acquiredAt: timestamp("acquired_at", { withTimezone: true }).notNull().defaultNow(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  lastResult: jsonb("last_result"),
  // PTW CAP-9 (Task #1358) — Background-Workers Observability columns.
  // Exposed by `GET /api/jobs/health` and rendered on the tenant Settings
  // "Job Health" tile so Tenant Admins can see whether the three PTW-side
  // background jobs (`ptw_auto_suspend`, `gas_retest_enforcement`,
  // `stalled_job_reaper`) are healthy without paging the SA console.
  // All columns are nullable / default zero so the change is non-breaking
  // for callers that ignore them.
  lastStartedAt: timestamp("last_started_at", { withTimezone: true }),
  lastFinishedAt: timestamp("last_finished_at", { withTimezone: true }),
  lastStatus: text("last_status"), // 'running' | 'ok' | 'error' | 'skipped'
  lastError: text("last_error"),
  runCount: integer("run_count").notNull().default(0),
  failureCount: integer("failure_count").notNull().default(0),
});
export const insertSchedulerLockSchema = createInsertSchema(schedulerLocks);
export type InsertSchedulerLock = z.infer<typeof insertSchedulerLockSchema>;
export type SchedulerLock = typeof schedulerLocks.$inferSelect;

// ─── Pack 6 (Task #1052): Multi-Server Resilience ──────────────────────────
// Postgres-backed rate limit counters. Replaces the per-process in-memory
// `Map` in `server/utils/sa-rate-limit.ts` so all instances share a single
// fixed-window counter per (scope, key).
export const rateLimitCounters = pgTable("rate_limit_counters", {
  scope: text("scope").notNull(),
  key: text("key").notNull(),
  windowStart: timestamp("window_start", { withTimezone: true }).notNull(),
  windowEnd: timestamp("window_end", { withTimezone: true }).notNull(),
  count: integer("count").notNull().default(1),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  pk: primaryKey({ columns: [t.scope, t.key, t.windowStart] }),
  idxWindowEnd: index("idx_rate_limit_counters_window_end").on(t.windowEnd),
}));
export type RateLimitCounter = typeof rateLimitCounters.$inferSelect;

// Cross-instance cache invalidation log. Mutators write a row; every
// instance polls every 5 s for new rows and busts the matching local cache.
export const cacheInvalidations = pgTable("cache_invalidations", {
  id: bigserial("id", { mode: "number" }).primaryKey(),
  channel: text("channel").notNull(),
  key: text("key").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxId: index("idx_cache_invalidations_id").on(t.id),
  idxCreatedAt: index("idx_cache_invalidations_created_at").on(t.createdAt),
}));
export type CacheInvalidation = typeof cacheInvalidations.$inferSelect;

// ─── Pack 9.D — Super Admin Operational Surfaces ────────────────────────────
// Append-only history of nightly platform dashboard aggregations. SA dashboards
// prefer the most recent row and fall back to live computation when empty.
export const platformDashboardSnapshots = pgTable("platform_dashboard_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  snapshotDate: timestamp("snapshot_date", { withTimezone: true }).notNull().defaultNow(),
  metrics: jsonb("metrics").notNull().$type<Record<string, unknown>>().default({}),
  generatedBy: text("generated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxSnapshotDate: index("idx_platform_dashboard_snapshots_snapshot_date").on(t.snapshotDate),
}));
export type PlatformDashboardSnapshot = typeof platformDashboardSnapshots.$inferSelect;
export const insertPlatformDashboardSnapshotSchema = createInsertSchema(platformDashboardSnapshots).omit({ id: true, createdAt: true });
export type InsertPlatformDashboardSnapshot = z.infer<typeof insertPlatformDashboardSnapshotSchema>;

// DB-backed feature kill list. Augments env/file precedence in
// server/utils/feature-kill-switch.ts so SAs can disable a flag without
// redeploying the pod. `kill_all=true` on any row disables every flag.
export const featureKillSwitches = pgTable("feature_kill_switches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  flagKey: text("flag_key").notNull(),
  killAll: boolean("kill_all").notNull().default(false),
  killed: boolean("killed").notNull().default(true),
  reason: text("reason"),
  actorId: text("actor_id"),
  actorName: text("actor_name"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  uqFlagKey: unique("uq_feature_kill_switches_flag_key").on(t.flagKey),
}));
export type FeatureKillSwitch = typeof featureKillSwitches.$inferSelect;
export const insertFeatureKillSwitchSchema = createInsertSchema(featureKillSwitches).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertFeatureKillSwitch = z.infer<typeof insertFeatureKillSwitchSchema>;

// ─── SA Dashboard Snapshots (Pack 9.D) ───────────────────────────────────────
// Pre-aggregated nightly snapshot of the metrics shown on /super-admin/dashboard.
// snapshotKind allows multiple snapshot families (platform_stats, partner_health,
// background_jobs, etc.) to coexist keyed by date.
export const saDashboardSnapshots = pgTable("sa_dashboard_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  snapshotDate: text("snapshot_date").notNull(),
  snapshotKind: text("snapshot_kind").notNull().default("platform_stats"),
  payload: jsonb("payload").$type<Record<string, unknown>>().notNull().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqDateKind: unique("sa_dashboard_snapshots_date_kind_unique").on(t.snapshotDate, t.snapshotKind),
  idxKindDate: index("sa_dashboard_snapshots_kind_date_idx").on(t.snapshotKind, t.snapshotDate),
}));
export const insertSaDashboardSnapshotSchema = createInsertSchema(saDashboardSnapshots).omit({ id: true, createdAt: true });
export type InsertSaDashboardSnapshot = z.infer<typeof insertSaDashboardSnapshotSchema>;
export type SaDashboardSnapshot = typeof saDashboardSnapshots.$inferSelect;

// ─── Pack 13.K — Billing & Commercial Readiness (Stripe) ────────────────────
//
// Three tables wire the canonical commercial-tier matrix into Stripe:
//
//   tenant_billing       — 1:1 to tenants. Mirrors the Stripe customer +
//                          subscription state needed for entitlement
//                          decisions and the in-app dunning banner.
//   billing_events       — append-only journal of every Stripe webhook
//                          event we processed; used for replay + audit.
//   usage_meter_records  — per-tenant per-meter counter with the last
//                          Stripe-reported high-water mark; the metering
//                          service only reports the delta above
//                          `lastReportedQuantity` to avoid double-billing.
//
// The whole surface degrades gracefully when no Stripe connector is wired:
// rows are still maintained (mode=`unconfigured`) so the resolver behaves
// identically and the operator can connect Stripe later without a backfill.

export const tenantBilling = pgTable("tenant_billing", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().unique().references(() => tenants.id, { onDelete: "cascade" }),
  // Stripe linkage. All optional — populated lazily on first checkout
  // attempt or on first webhook event (whichever arrives first).
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  stripePriceId: text("stripe_price_id"),
  // Mirror of the Stripe subscription status verbatim (active, trialing,
  // past_due, canceled, unpaid, incomplete, incomplete_expired,
  // paused). When no Stripe connector is wired the value is "unconfigured".
  status: text("status").notNull().default("unconfigured"),
  // "test" | "live" | "unconfigured" — mirrors the Stripe key in use at
  // the time of the last write. Lets the SA dashboard tell the operator
  // when test-mode billing data is masquerading as production state.
  mode: text("mode").notNull().default("unconfigured"),
  // Plan-tier key (matches subscription_tiers.key). Held alongside the
  // Stripe price id so the resolver can short-circuit without round-
  // tripping to Stripe and so unconfigured tenants still expose a tier.
  planKey: text("plan_key"),
  currentPeriodEnd: timestamp("current_period_end", { withTimezone: true }),
  cancelAt: timestamp("cancel_at", { withTimezone: true }),
  // Dunning state — set when a payment fails, cleared on the next
  // payment-succeeded event. `dunningGracePeriodEnd` is the deadline
  // before feature suspension; the in-app banner reads from here.
  dunningState: text("dunning_state").notNull().default("none"), // none | grace | suspended
  dunningStartedAt: timestamp("dunning_started_at", { withTimezone: true }),
  dunningGracePeriodEnd: timestamp("dunning_grace_period_end", { withTimezone: true }),
  dunningLastReason: text("dunning_last_reason"),
  // Wallclock of the last successful provider-event apply. Used by the
  // dashboard staleness indicator and by replay.
  lastEventAt: timestamp("last_event_at", { withTimezone: true }),
  lastEventId: text("last_event_id"),
  metadata: jsonb("metadata").$type<Record<string, unknown>>().notNull().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxTenantBillingStripeCustomer: index("idx_tenant_billing_stripe_customer").on(t.stripeCustomerId),
  idxTenantBillingDunning: index("idx_tenant_billing_dunning_state").on(t.dunningState),
}));
export const insertTenantBillingSchema = createInsertSchema(tenantBilling).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTenantBilling = z.infer<typeof insertTenantBillingSchema>;
export type TenantBilling = typeof tenantBilling.$inferSelect;

export const billingEvents = pgTable("billing_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Stripe event id (`evt_…`). Unique so a duplicate webhook delivery
  // is a no-op (idempotency key). For non-Stripe events we synthesise
  // a `manual_<uuid>` id so the unique constraint still holds.
  providerEventId: text("provider_event_id").notNull().unique(),
  provider: text("provider").notNull().default("stripe"),
  eventType: text("event_type").notNull(),
  tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "set null" }),
  // Outcome classification used by the dashboard / replay tooling.
  // "applied" — successfully mutated tenant_billing
  // "ignored" — recognised type but no-op (e.g. test-mode in live env)
  // "failed"  — handler threw; row is preserved so we can replay
  status: text("status").notNull().default("applied"),
  failureReason: text("failure_reason"),
  rawPayload: jsonb("raw_payload").$type<Record<string, unknown>>().notNull(),
  receivedAt: timestamp("received_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxBillingEventsTenant: index("idx_billing_events_tenant_received").on(t.tenantId, t.receivedAt),
  idxBillingEventsType: index("idx_billing_events_type").on(t.eventType),
}));
export const insertBillingEventSchema = createInsertSchema(billingEvents).omit({ id: true, receivedAt: true });
export type InsertBillingEvent = z.infer<typeof insertBillingEventSchema>;
export type BillingEvent = typeof billingEvents.$inferSelect;

export const usageMeterRecords = pgTable("usage_meter_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  // Canonical meter key. Matches the Stripe meter event_name and is the
  // public contract used by usage-priced SKUs (e.g. `workers.active`,
  // `api.calls`, `bulk.jobs`).
  meterKey: text("meter_key").notNull(),
  // Total quantity observed locally for the current Stripe billing
  // period. Reset by the period_end webhook handler.
  currentQuantity: integer("current_quantity").notNull().default(0),
  // High-water mark of what we have already reported to Stripe. The
  // metering service only ships `currentQuantity - lastReportedQuantity`
  // so a re-report can never double-count.
  lastReportedQuantity: integer("last_reported_quantity").notNull().default(0),
  lastReportedAt: timestamp("last_reported_at", { withTimezone: true }),
  // Stripe meter event id we used for the last report — pinned for
  // troubleshooting + idempotency.
  lastEventId: text("last_event_id"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  uniqTenantMeter: unique("usage_meter_records_tenant_meter_unique").on(t.tenantId, t.meterKey),
  idxUsageMeterTenant: index("idx_usage_meter_records_tenant").on(t.tenantId),
}));
export const insertUsageMeterRecordSchema = createInsertSchema(usageMeterRecords).omit({ id: true, updatedAt: true });
export type InsertUsageMeterRecord = z.infer<typeof insertUsageMeterRecordSchema>;
export type UsageMeterRecord = typeof usageMeterRecords.$inferSelect;

// ─── Pack 13.H — Customer-Facing Status Page ──────────────────────────────
// Public status page substrate. All four tables are platform-scoped (no
// tenantId): customers see a single global view of platform health.
//
// Tables:
//   - status_components      — stable list of components shown on the page
//   - status_incidents       — open + historical incidents/maintenance
//   - status_incident_updates— investigating → identified → monitoring → resolved
//   - status_subscriptions   — email subscribers (RSS is anonymous)

export const STATUS_COMPONENT_STATES = [
  "operational",
  "degraded_performance",
  "partial_outage",
  "major_outage",
  "under_maintenance",
] as const;
export type StatusComponentState = (typeof STATUS_COMPONENT_STATES)[number];

export const STATUS_INCIDENT_STATES = [
  "investigating",
  "identified",
  "monitoring",
  "resolved",
] as const;
export type StatusIncidentState = (typeof STATUS_INCIDENT_STATES)[number];

export const STATUS_INCIDENT_IMPACTS = [
  "none",
  "minor",
  "major",
  "critical",
  "maintenance",
] as const;
export type StatusIncidentImpact = (typeof STATUS_INCIDENT_IMPACTS)[number];

export const statusComponents = pgTable("status_components", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  currentStatus: text("current_status").notNull().default("operational"),
  syntheticProbeKey: text("synthetic_probe_key"),
  lastCheckedAt: timestamp("last_checked_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxSortOrder: index("idx_status_components_sort_order").on(t.sortOrder),
}));
export type StatusComponent = typeof statusComponents.$inferSelect;
export const insertStatusComponentSchema = createInsertSchema(statusComponents).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertStatusComponent = z.infer<typeof insertStatusComponentSchema>;

export const statusIncidents = pgTable("status_incidents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  status: text("status").notNull().default("investigating"),
  impact: text("impact").notNull().default("minor"),
  affectedComponents: text("affected_components").array().notNull().default(sql`ARRAY[]::text[]`),
  isMaintenance: boolean("is_maintenance").notNull().default(false),
  scheduledFor: timestamp("scheduled_for", { withTimezone: true }),
  scheduledUntil: timestamp("scheduled_until", { withTimezone: true }),
  startedAt: timestamp("started_at", { withTimezone: true }).notNull().defaultNow(),
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxStatus: index("idx_status_incidents_status").on(t.status),
  idxStartedAt: index("idx_status_incidents_started_at").on(t.startedAt),
  idxResolvedAt: index("idx_status_incidents_resolved_at").on(t.resolvedAt),
}));
export type StatusIncident = typeof statusIncidents.$inferSelect;
export const insertStatusIncidentSchema = createInsertSchema(statusIncidents).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertStatusIncident = z.infer<typeof insertStatusIncidentSchema>;

export const statusIncidentUpdates = pgTable("status_incident_updates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  incidentId: varchar("incident_id").notNull().references(() => statusIncidents.id, { onDelete: "cascade" }),
  status: text("status").notNull(),
  message: text("message").notNull(),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxIncidentCreatedAt: index("idx_status_incident_updates_incident_created").on(t.incidentId, t.createdAt),
}));
export type StatusIncidentUpdate = typeof statusIncidentUpdates.$inferSelect;
export const insertStatusIncidentUpdateSchema = createInsertSchema(statusIncidentUpdates).omit({ id: true, createdAt: true });
export type InsertStatusIncidentUpdate = z.infer<typeof insertStatusIncidentUpdateSchema>;

export const statusSubscriptions = pgTable("status_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull(),
  componentKeys: text("component_keys").array().notNull().default(sql`ARRAY[]::text[]`),
  confirmationToken: text("confirmation_token"),
  confirmedAt: timestamp("confirmed_at", { withTimezone: true }),
  unsubscribeToken: text("unsubscribe_token").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  uqEmail: unique("uq_status_subscriptions_email").on(t.email),
  idxConfirmed: index("idx_status_subscriptions_confirmed").on(t.confirmedAt),
}));
export type StatusSubscription = typeof statusSubscriptions.$inferSelect;
export const insertStatusSubscriptionSchema = createInsertSchema(statusSubscriptions).omit({ id: true, createdAt: true });
export type InsertStatusSubscription = z.infer<typeof insertStatusSubscriptionSchema>;

// ─── Pack 11.B (Task #1030) — Platform-Wide Defaults ───────────────────────
// Replaces draft #299. A single platform-wide "active" defaults row holds
// the canonical set of pre-configured settings every newly-provisioned
// tenant inherits. The platform team edits this row from the SA console
// and bumps `version`; existing tenants can opt-in to "apply latest
// defaults" via the Pack 9.G-style preview / decisions flow.
//
// `payload` carries six bounded sections (see TENANT_DEFAULTS_SECTION_KEYS
// below). The shape is intentionally flat-jsonb rather than a wide column
// set so the platform team can extend it without schema migrations.
export const TENANT_DEFAULTS_SECTION_KEYS = [
  "features",
  "notifications",
  "approvalDefaults",
  "severityMatrix",
  "safetyCategories",
  "equipmentTypes",
  "documentCategories",
  // PTW umbrella section. Currently carries:
  //  • Phase 8.D (Task #1283) — preset pack catalogue (`presetPacks`)
  //  • CAP-21 (Task #1374) — permit numbering scheme (`numbering`)
  // Future PTW sub-keys (PDF defaults, retention windows, etc.) extend
  // the same `ptw` umbrella without a section-keys churn.
  "ptw",
] as const;
export type TenantDefaultsSectionKey = typeof TENANT_DEFAULTS_SECTION_KEYS[number];

/**
 * PTW Phase 8.D (Task #1283) — Reference to a published preset pack the
 * platform team has made available to tenants. The pack content itself lives
 * in `server/data/ptw-preset-packs.ts`; this entry exists in the platform
 * defaults payload so the SA UI knows which packs are catalogued and which
 * version is currently published.
 */
export interface TenantDefaultsPtwPresetPackEntry {
  /** Stable pack id (e.g. `"og-upstream"`, `"gcc-overlay"`). */
  packId: string;
  /** Currently published version (1, 2, …). */
  version: number;
  /** Detached deterministic signature for the published pack content. */
  signature: string;
  /** Notes / changelog for this version. */
  notes?: string;
}

/**
 * PTW CAP-21 (Task #1374) — Default permit numbering scheme carried in
 * `tenant_defaults.payload.ptw.numbering`. Mirrors the per-row column
 * shape on `ptw_numbering_schemes` so the SA "promote default" flow can
 * lift the default into a tenant-scoped row 1:1.
 */
export interface PtwNumberingPayloadDefault {
  name: string;
  template: string;
  counterStrategy:
    | "per-tenant"
    | "per-site"
    | "per-month"
    | "per-year"
    | "per-permit-type"
    | "per-operator";
  resetCadence: "none" | "yearly" | "monthly";
  padding: number;
  seqStart?: number;
}

export interface TenantDefaultsPayload {
  features?: Record<string, boolean>;
  notifications?: {
    emailEnabled?: boolean;
    inAppEnabled?: boolean;
    digestFrequency?: "off" | "daily" | "weekly";
  };
  approvalDefaults?: {
    requireDualApproval?: boolean;
    autoEscalateAfterHours?: number;
  };
  severityMatrix?: Record<string, "minor" | "moderate" | "major" | "critical">;
  safetyCategories?: string[];
  equipmentTypes?: string[];
  documentCategories?: string[];
  /**
   * PTW-module defaults. Carries:
   *  • Phase 8.D (Task #1283) — `presetPacks`: platform-curated preset
   *    catalogue. Tenants opt-in via the adoption flow which records to
   *    `ptw_preset_pack_adoptions`.
   *  • CAP-21 (Task #1374) — `numbering`: platform-default permit
   *    numbering scheme inherited at provisioning time.
   * Future PTW-wide defaults (PDF cover pages, retention windows, etc.)
   * extend the same `ptw` object.
   */
  ptw?: {
    presetPacks?: TenantDefaultsPtwPresetPackEntry[];
    numbering?: PtwNumberingPayloadDefault;
  };
}

export const tenantDefaults = pgTable("tenant_defaults", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  version: integer("version").notNull().default(1),
  payload: jsonb("payload").$type<TenantDefaultsPayload>().notNull().default({}),
  notes: text("notes"),
  isActive: boolean("is_active").notNull().default(true),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertTenantDefaultsSchema = createInsertSchema(tenantDefaults).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertTenantDefaults = z.infer<typeof insertTenantDefaultsSchema>;
export type TenantDefaults = typeof tenantDefaults.$inferSelect;

export const TENANT_DEFAULTS_TRIGGERS = ["provisioning", "manual_apply"] as const;
export type TenantDefaultsTrigger = typeof TENANT_DEFAULTS_TRIGGERS[number];

export interface TenantDefaultsDecision {
  /** Dot-notation key, e.g. "notifications.digestFrequency". */
  key: string;
  /** "accept" overwrites tenant value with platform default; "reject" keeps current tenant value. */
  action: "accept" | "reject";
}

export interface TenantDefaultsApplyResult {
  applied: string[];
  skipped: string[];
  unchanged: string[];
}

export const tenantDefaultsApplicationLog = pgTable("tenant_defaults_application_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  defaultsId: varchar("defaults_id").notNull(),
  defaultsVersion: integer("defaults_version").notNull(),
  trigger: text("trigger").notNull(),
  decisions: jsonb("decisions").$type<TenantDefaultsDecision[]>().notNull().default([]),
  result: jsonb("result").$type<TenantDefaultsApplyResult>().notNull().default({ applied: [], skipped: [], unchanged: [] }),
  appliedBy: text("applied_by"),
  appliedAt: timestamp("applied_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxTenantDefaultsAppLogTenant: index("idx_tenant_defaults_application_log_tenant").on(t.tenantId),
}));
export const insertTenantDefaultsApplicationLogSchema = createInsertSchema(tenantDefaultsApplicationLog).omit({
  id: true,
  appliedAt: true,
});
export type InsertTenantDefaultsApplicationLog = z.infer<typeof insertTenantDefaultsApplicationLogSchema>;
export type TenantDefaultsApplicationLog = typeof tenantDefaultsApplicationLog.$inferSelect;

// ─── PTW Phase 8.D (Task #1283) — Standalone onboarding + preset packs ───
//
// `ptw_onboarding_log` records each step the SA completes through the
// standalone PTW onboarding wizard so we keep an audit trail of what an
// empty tenant did to become operational. The wizard writes one row per
// step (`industry_selected`, `pack_selected`, `terminology_set`,
// `numbering_set`, `template_reviewed`, `roles_assigned`, `dry_run_completed`,
// `wizard_completed`).
export const PTW_ONBOARDING_STEPS = [
  "industry_selected",
  "pack_selected",
  "terminology_set",
  "numbering_set",
  "template_reviewed",
  "roles_assigned",
  "dry_run_completed",
  "wizard_completed",
] as const;
export type PtwOnboardingStep = typeof PTW_ONBOARDING_STEPS[number];

export const ptwOnboardingLog = pgTable("ptw_onboarding_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  step: text("step").notNull(),
  payload: jsonb("payload").$type<Record<string, unknown>>().notNull().default({}),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxPtwOnboardingLogTenant: index("idx_ptw_onboarding_log_tenant").on(t.tenantId),
  idxPtwOnboardingLogStep: index("idx_ptw_onboarding_log_step").on(t.tenantId, t.step),
}));
export const insertPtwOnboardingLogSchema = createInsertSchema(ptwOnboardingLog).omit({
  id: true,
  createdAt: true,
});
export type InsertPtwOnboardingLog = z.infer<typeof insertPtwOnboardingLogSchema>;
export type PtwOnboardingLogEntry = typeof ptwOnboardingLog.$inferSelect;

// `ptw_preset_pack_adoptions` tracks which preset packs (by id + version) a
// tenant has adopted, what decisions were made on each section
// (`accept` / `reject` per pack subkey), and the materialised result. This
// is the per-pack equivalent of `tenant_defaults_application_log` — kept as
// its own table because the cardinality is per-pack/version, not per-defaults
// row.
export interface PtwPresetPackDecision {
  /** Sub-key inside the pack (e.g. `terminology`, `numbering`, `templates`,
   *  `hazards`, `simops`, `capacity`, `retention`, `language`). */
  key: string;
  action: "accept" | "reject";
}

export interface PtwPresetPackApplyResult {
  applied: string[];
  skipped: string[];
  unchanged: string[];
  /** Materialised template ids the installer created during this adoption. */
  installedTemplateIds?: string[];
}

export const ptwPresetPackAdoptions = pgTable("ptw_preset_pack_adoptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  packId: text("pack_id").notNull(),
  packVersion: integer("pack_version").notNull(),
  packSignature: text("pack_signature").notNull(),
  decisions: jsonb("decisions").$type<PtwPresetPackDecision[]>().notNull().default([]),
  result: jsonb("result").$type<PtwPresetPackApplyResult>().notNull().default({ applied: [], skipped: [], unchanged: [] }),
  rollback: boolean("rollback").notNull().default(false),
  rollbackOfId: varchar("rollback_of_id"),
  adoptedBy: text("adopted_by"),
  adoptedAt: timestamp("adopted_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxPtwPresetPackAdoptionsTenant: index("idx_ptw_preset_pack_adoptions_tenant").on(t.tenantId),
  idxPtwPresetPackAdoptionsPack: index("idx_ptw_preset_pack_adoptions_pack").on(t.tenantId, t.packId),
}));
export const insertPtwPresetPackAdoptionSchema = createInsertSchema(ptwPresetPackAdoptions).omit({
  id: true,
  adoptedAt: true,
});
export type InsertPtwPresetPackAdoption = z.infer<typeof insertPtwPresetPackAdoptionSchema>;
export type PtwPresetPackAdoption = typeof ptwPresetPackAdoptions.$inferSelect;

// ─── Task #3906: DB-configurable Industry Recommendations ────────────────────
// Stores per-industry module/tier recommendations shown in the tenant creation wizard.
// Platform-scoped (no tenant_id). Seeded from hardcoded defaults at boot.
export const industryProfileRecommendations = pgTable("industry_profile_recommendations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  industryType: text("industry_type").notNull().unique(),
  recommendedTier: text("recommended_tier").notNull().default("standard"),
  blueprintLabel: text("blueprint_label").notNull().default(""),
  recommendedModuleAliases: text("recommended_module_aliases").array().notNull().default(sql`'{}'::text[]`),
  suggestedPackSlug: text("suggested_pack_slug"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxIndustryProfileRecType: index("idx_industry_profile_recommendations_type").on(t.industryType),
}));
export const insertIndustryProfileRecommendationSchema = createInsertSchema(industryProfileRecommendations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertIndustryProfileRecommendation = z.infer<typeof insertIndustryProfileRecommendationSchema>;
export type IndustryProfileRecommendation = typeof industryProfileRecommendations.$inferSelect;

// ─── Task #3906: DB-configurable Operational Profile Definitions ─────────────
// Stores the feature-flag/route/nav-group definition for each operational profile type.
// Platform-scoped (no tenant_id). Seeded from OPERATIONAL_PROFILES at boot.
export const operationalProfileDefinitions = pgTable("operational_profile_definitions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  profileType: text("profile_type").notNull().unique(),
  label: text("label").notNull(),
  description: text("description").notNull().default(""),
  enabledFeatureFlags: text("enabled_feature_flags").array().notNull().default(sql`'{}'::text[]`),
  visibleNavGroups: text("visible_nav_groups").array().notNull().default(sql`'{}'::text[]`),
  defaultLandingRoute: text("default_landing_route").notNull().default("/"),
  partnerCapabilityDefaults: text("partner_capability_defaults").array().notNull().default(sql`'{}'::text[]`),
  isSystem: boolean("is_system").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxOperationalProfileDefType: index("idx_operational_profile_definitions_type").on(t.profileType),
}));
export const insertOperationalProfileDefinitionSchema = createInsertSchema(operationalProfileDefinitions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertOperationalProfileDefinition = z.infer<typeof insertOperationalProfileDefinitionSchema>;
export type OperationalProfileDefinition = typeof operationalProfileDefinitions.$inferSelect;
